'use client'
import React, { useState, useEffect } from 'react'
import { useAnimation } from '@/providers/Animation'


interface CloudProps {
    src: string
    top: string
    left: string
    width: string
    duration: number
    delay: number
    opacity: number
    morphDuration: number
    floatDuration: number
    colorDuration: number
}

const Cloud: React.FC<CloudProps> = ({
    src, top, left, width, duration, delay, opacity,
    morphDuration, floatDuration, colorDuration
}) => {
    return (
        <div
            className="absolute pointer-events-none"
            style={{
                top: top,
                left: left,
                width: width,
                height: 'auto',
                // Main movement wrapper
                animation: `moveCloud ${duration}s linear infinite`,
                animationDelay: `${delay}s`,
                opacity: opacity,
            }}
        >
            {/* Inner wrapper for floating (vertical bobbing) */}
            <div
                className="cloud-float"
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    animation: `floatCloud ${floatDuration}s ease-in-out infinite alternate`,
                }}
            >
                {/* 1. Invisible spacer to maintain aspect ratio */}
                <img
                    src={src}
                    alt=""
                    className="w-full h-auto opacity-0 cloud-morph"
                    style={{
                        animation: `morphCloud ${morphDuration}s ease-in-out infinite alternate`,
                    }}
                />

                {/* 2. The visible cloud (Masked Div) - Color is controlled via background-color */}
                <div
                    className="cloud-visible"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: '#ffffff', // Initial color for visibility
                        maskImage: `url(${src})`,
                        WebkitMaskImage: `url(${src})`,
                        maskSize: '100% 100%',
                        WebkitMaskSize: '100% 100%',
                        maskRepeat: 'no-repeat',
                        WebkitMaskRepeat: 'no-repeat',
                        // Animation combines morphing (transform) and color shift (bg-color)
                        animation: `
                            morphCloud ${morphDuration}s ease-in-out infinite alternate,
                            changeColor ${colorDuration}s ease-in-out infinite alternate
                        `,
                    }}
                />
            </div>
        </div>
    )
}

// Base cloud configurations without random values
const baseCloudConfigs = [
    // Layer 1 - Slow, big clouds (Background)
    { src: '/cloud-1.png', top: '10%', left: '-20%', width: '400px', duration: 120, delay: 0, opacity: 0.3, baseMorph: 15, baseFloat: 20, baseColor: 25 },
    { src: '/cloud-2.png', top: '30%', left: '-20%', width: '500px', duration: 140, delay: -50, opacity: 0.2, baseMorph: 18, baseFloat: 22, baseColor: 28 },
    { src: '/cloud-1.png', top: '5%', left: '-20%', width: '450px', duration: 130, delay: -90, opacity: 0.25, baseMorph: 16, baseFloat: 19, baseColor: 30 },
    // Layer 2 - Medium clouds (Midground)
    { src: '/cloud-2.png', top: '40%', left: '-20%', width: '300px', duration: 80, delay: -20, opacity: 0.4, baseMorph: 12, baseFloat: 15, baseColor: 20 },
    { src: '/cloud-3.png', top: '15%', left: '-20%', width: '250px', duration: 90, delay: -60, opacity: 0.35, baseMorph: 14, baseFloat: 17, baseColor: 22 },
    { src: '/cloud-1.png', top: '60%', left: '-20%', width: '350px', duration: 85, delay: -10, opacity: 0.3, baseMorph: 13, baseFloat: 16, baseColor: 21 },
    // Layer 3 - Fast, small clouds (Foreground)
    { src: '/cloud-3.png', top: '25%', left: '-20%', width: '150px', duration: 60, delay: -30, opacity: 0.5, baseMorph: 8, baseFloat: 10, baseColor: 15 },
    { src: '/cloud-2.png', top: '50%', left: '-20%', width: '180px', duration: 65, delay: -70, opacity: 0.45, baseMorph: 9, baseFloat: 11, baseColor: 16 },
    // Layer 4 - Extra variety (New assets)
    { src: '/cloud-4.png', top: '70%', left: '-20%', width: '600px', duration: 150, delay: -110, opacity: 0.15, baseMorph: 20, baseFloat: 25, baseColor: 35 },
    { src: '/cloud-5.png', top: '20%', left: '-20%', width: '320px', duration: 95, delay: -40, opacity: 0.3, baseMorph: 14, baseFloat: 16, baseColor: 25 },
]

export const CloudLayer = () => {
    const { animationsEnabled } = useAnimation()

    // Use stable initial values for SSR, then randomize on client
    const [clouds, setClouds] = useState<CloudProps[]>(() =>
        baseCloudConfigs.map(config => ({
            src: config.src,
            top: config.top,
            left: config.left,
            width: config.width,
            duration: config.duration,
            delay: config.delay,
            opacity: config.opacity,
            morphDuration: config.baseMorph,
            floatDuration: config.baseFloat,
            colorDuration: config.baseColor,
        }))
    )

    const [isClient, setIsClient] = useState(false)

    // Only randomize on client after hydration
    useEffect(() => {
        setIsClient(true)
        setClouds(
            baseCloudConfigs.map(config => ({
                src: config.src,
                top: config.top,
                left: config.left,
                width: config.width,
                duration: config.duration,
                delay: config.delay,
                opacity: config.opacity,
                morphDuration: config.baseMorph + Math.random() * 10,
                floatDuration: config.baseFloat + Math.random() * 10,
                colorDuration: config.baseColor + Math.random() * 15,
            }))
        )
    }, [])

    // Don't render until client-side to avoid hydration mismatch
    if (!isClient || !animationsEnabled) {
        return null
    }

    return (
        <div
            className="fixed inset-0 pointer-events-none overflow-hidden"
            style={{ zIndex: 5 }}
        >
            <style jsx global>{`
                @keyframes moveCloud {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(120vw); }
                }
                @keyframes morphCloud {
                    0% { transform: scale(1) skewX(0deg); }
                    50% { transform: scale(1.05, 0.95) skewX(2deg); }
                    100% { transform: scale(0.95, 1.05) skewX(-2deg); }
                }
                @keyframes floatCloud {
                    0% { transform: translateY(0); }
                    100% { transform: translateY(-20px); }
                }
                @keyframes changeColor {
                    0% { background-color: #ffffff; }
                    50% { background-color: #ffccff; } /* Soft Pink */
                    100% { background-color: #ccccff; } /* Soft Blue */
                }
                /* Disable expensive per-cloud animations on mobile — keep only the basic drift */
                @media (max-width: 768px) {
                    .cloud-float,
                    .cloud-morph,
                    .cloud-visible {
                        animation: none !important;
                    }
                }
            `}</style>

            {clouds.map((cloud, i) => (
                <Cloud key={i} {...cloud} />
            ))}
        </div>
    )
}

